import { apiSlice } from './apiSlice';

export const personalWithdrawalsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getWithdrawalLifecycle: builder.query<any, { kind: 'inventory' | 'cash'; id: number | string }>({
      query: ({ kind, id }) => ({ url: `/withdrawal-lifecycle/${kind}/${id}` }),
      providesTags: ['PersonalWithdrawal', 'CashWithdrawal'],
    }),
    receiveWithdrawalReturn: builder.mutation<any, { kind: 'inventory' | 'cash'; id: number | string; data: any }>({
      query: ({ kind, id, data }) => ({ url: `/withdrawal-lifecycle/${kind}/${id}/returns`, method: 'POST', data }),
      invalidatesTags: ['PersonalWithdrawal', 'CashWithdrawal', 'Products', 'Accounting', 'AccountLedger', 'DashboardStats'],
    }),
    requestWithdrawalCorrection: builder.mutation<any, { kind: 'inventory' | 'cash'; id: number | string; data: any }>({
      query: ({ kind, id, data }) => ({ url: `/withdrawal-lifecycle/${kind}/${id}/corrections`, method: 'POST', data }),
      invalidatesTags: ['PersonalWithdrawal', 'CashWithdrawal'],
    }),
    reviewWithdrawalCorrection: builder.mutation<any, { kind: 'inventory' | 'cash'; id: number | string; correction: number; data: any }>({
      query: ({ kind, id, correction, data }) => ({ url: `/withdrawal-lifecycle/${kind}/${id}/corrections/${correction}/review`, method: 'POST', data }),
      invalidatesTags: ['PersonalWithdrawal', 'CashWithdrawal', 'Products', 'Accounting', 'AccountLedger', 'DashboardStats'],
    }),
    // Inventory Personal Withdrawals
    getPersonalWithdrawals: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/personal_withdrawals',
        params: params || {},
      }),
      providesTags: ['PersonalWithdrawal'],
    }),
    getPersonalWithdrawal: builder.query<any, number | string>({
      query: (id) => ({ url: `/personal_withdrawals/${id}/edit` }),
      providesTags: (result, error, id) => [{ type: 'PersonalWithdrawal', id }],
    }),
    deletePersonalWithdrawal: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/personal_withdrawals/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PersonalWithdrawal', 'Products', 'DashboardStats', 'Accounting', 'AccountLedger'],
    }),
    deleteSelectedPersonalWithdrawals: builder.mutation<any, { selectedIds?: (number | string)[]; ids?: (number | string)[] }>({
      query: (body) => {
        const selectedIds = body?.selectedIds || body?.ids || [];
        // Never POST an empty selection: the by_selection endpoint would
        // receive no ids (mass-delete/no-op risk). Fail fast instead.
        if (selectedIds.length === 0) {
          throw new Error('No personal withdrawals selected for deletion');
        }
        return {
          url: '/personal_withdrawals/delete/by_selection',
          method: 'POST',
          data: { selectedIds },
        };
      },
      invalidatesTags: ['PersonalWithdrawal', 'Products', 'DashboardStats', 'Accounting', 'AccountLedger'],
    }),

    // Cash Withdrawals
    getCashWithdrawals: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/personal_withdrawals/cash',
        params: params || {},
      }),
      providesTags: ['CashWithdrawal'],
    }),
    getCashWithdrawal: builder.query<any, number | string>({
      query: (id) => ({ url: `/personal_withdrawals/cash/${id}` }),
      providesTags: (result, error, id) => [{ type: 'CashWithdrawal', id }],
    }),
    deleteCashWithdrawal: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/personal_withdrawals/cash/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CashWithdrawal', 'Accounting', 'AccountLedger', 'DashboardStats'],
    }),
    deleteSelectedCashWithdrawals: builder.mutation<any, { selectedIds?: (number | string)[]; ids?: (number | string)[] }>({
      query: (body) => {
        const selectedIds = body?.selectedIds || body?.ids || [];
        // Same empty-selection guard as the personal-withdrawal bulk delete.
        if (selectedIds.length === 0) {
          throw new Error('No cash withdrawals selected for deletion');
        }
        return {
          url: '/personal_withdrawals/cash/delete/by_selection',
          method: 'POST',
          data: { selectedIds },
        };
      },
      invalidatesTags: ['CashWithdrawal', 'Accounting', 'AccountLedger', 'DashboardStats'],
    }),
    updateCashWithdrawalStatus: builder.mutation<any, { id: number | string; payload: any }>({
      query: ({ id, payload }) => ({
        url: `/personal_withdrawals/cash/${id}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'CashWithdrawal', id }, 'CashWithdrawal', 'Accounting', 'AccountLedger', 'DashboardStats'],
    }),
    getPersonalWithdrawalDetail: builder.query<any, string | number>({
      query: (id) => ({ url: `/personal_withdrawals/${id}` }),
      providesTags: (result, error, id) => [{ type: 'PersonalWithdrawal', id }],
    }),
    getWithdrawalProductsByWarehouse: builder.query<any[], { warehouseId: string | number; params?: Record<string, any> }>({
      query: ({ warehouseId, params }) => ({
        url: `/get_products_by_warehouse/${warehouseId}`,
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    getCreateData: builder.query<any, void>({
      query: () => ({ url: '/personal_withdrawals/create' }),
    }),
    createPersonalWithdrawal: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/personal_withdrawals',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['PersonalWithdrawal', 'Products', 'DashboardStats', 'Accounting', 'AccountLedger'],
    }),
    updatePersonalWithdrawal: builder.mutation<any, { id: number | string; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/personal_withdrawals/${id}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'PersonalWithdrawal', id }, 'PersonalWithdrawal', 'Products', 'DashboardStats', 'Accounting', 'AccountLedger'],
    }),
  }),
});

export const {
  useGetWithdrawalLifecycleQuery, useReceiveWithdrawalReturnMutation, useRequestWithdrawalCorrectionMutation, useReviewWithdrawalCorrectionMutation,
  useGetPersonalWithdrawalsQuery,
  useGetPersonalWithdrawalQuery,
  useLazyGetPersonalWithdrawalQuery,
  useDeletePersonalWithdrawalMutation,
  useDeleteSelectedPersonalWithdrawalsMutation,

  useGetCashWithdrawalsQuery,
  useGetCashWithdrawalQuery,
  useDeleteCashWithdrawalMutation,
  useDeleteSelectedCashWithdrawalsMutation,
  useUpdateCashWithdrawalStatusMutation,
  useGetPersonalWithdrawalDetailQuery,
  useLazyGetPersonalWithdrawalDetailQuery,
  useGetWithdrawalProductsByWarehouseQuery: useGetProductsByWarehouseQuery,
  useLazyGetWithdrawalProductsByWarehouseQuery: useLazyGetProductsByWarehouseQuery,
  useLazyGetCreateDataQuery,
  useCreatePersonalWithdrawalMutation,
  useUpdatePersonalWithdrawalMutation,
} = personalWithdrawalsApi;
